import React, { useEffect, useRef, useState } from 'react';
import './index.css';
import { watchFrameDuration } from '../../../../Utils/videoFrameStep';

// Change to 2 or 3 for different arrow styles (for screenshots/approval)
// 1 = dark semi-transparent circles at edges (subtle)
// 2 = arrows overlaid ON the image itself
// 3 = ghost slim arrows, no background
const ARROW_VARIANT: 1 | 2 | 3 = 3;

const SizeToScreen = ({
  handleNavClick,
  handleTileDoubleClick,
  currIndex,
  mediaUrls,
  setCurrIndex,
  getPlaybackRate,
  isPlaying = true,
  // Parent's handle on the clip in view, for frame stepping.
  videoRef,
}: any) => {
  const applyRate = (el: HTMLVideoElement | null, url: string) => {
    if (!el || !getPlaybackRate) {
      return;
    }
    const rate = getPlaybackRate(url);
    if (typeof rate === 'number' && rate > 0) {
      el.playbackRate = rate;
    }
  };
  const [panelCollapsed, setPanelCollapsed] = useState(false);

  // The Doppler map and the saved rates both arrive from the network after the first
  // clip has already fired onLoadedData, so re-apply whenever the resolver changes —
  // otherwise the clip on screen at that moment stays stuck at 1x.
  useEffect(() => {
    applyRate(mainVideoRef.current, mediaUrls[currIndex]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currIndex, mediaUrls, getPlaybackRate]);

  // The dock's Play/Pause drives this — honour isPlaying on the visible loop
  // (the parent's videoRefs observer doesn't reach this component's video).
  const mainVideoRef = useRef<HTMLVideoElement | null>(null);
  useEffect(() => {
    const v = mainVideoRef.current;
    if (!v) {
      return;
    }
    if (isPlaying) {
      v.play().catch(() => {});
    } else {
      v.pause();
    }
  }, [isPlaying, currIndex]);

  const togglePanel = () => {
    setPanelCollapsed(!panelCollapsed);
  };

  const handleThumbnailClick = index => {
    setCurrIndex(index);
  };

  return (
    <div className="size__to__screen__container">
      {/* Left Panel */}
      <div className={`left__panel ${panelCollapsed ? 'collapsed' : ''}`}>
        <button
          className="panel__toggle"
          onClick={togglePanel}
        >
          {panelCollapsed ? '>' : '<'}
        </button>

        {!panelCollapsed && (
          <div className="thumbnails__container">
            {mediaUrls.map((url, index) => (
              <div
                key={index}
                className={`thumbnail__item ${index === currIndex ? 'active' : ''}`}
                onClick={() => handleThumbnailClick(index)}
              >
                {url.endsWith('.webm') ? (
                  <div className="video-thumbnail">
                    <video
                      src={url}
                      className="thumbnail__image"
                      onLoadedData={e => applyRate(e.currentTarget, url)}
                    />
                    <div className="play-icon">▶</div>
                  </div>
                ) : (
                  <img
                    src={url}
                    alt={`Thumbnail ${index}`}
                    className="thumbnail__image"
                  />
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Main Content */}
      <div className="size__to__screen__layout">
        {/* Variant 1 & 3: arrows outside image */}
        {ARROW_VARIANT !== 2 && (
          <button
            className={`sts-arrow sts-arrow--prev sts-arrow--v${ARROW_VARIANT}`}
            onClick={() => handleNavClick('prev')}
            aria-label="Previous"
          >
            &#8249;
          </button>
        )}

        {/* Media */}
        <div className="sts-media-wrap">
          <div
            className="sts-stage"
            onDoubleClick={() => handleTileDoubleClick(currIndex)}
          >
            {/* Variant 2: arrows overlaid on image, hugging the stage */}
            {ARROW_VARIANT === 2 && (
              <>
                <button
                  className="sts-arrow sts-arrow--prev sts-arrow--v2"
                  onClick={() => handleNavClick('prev')}
                  aria-label="Previous"
                >
                  &#8249;
                </button>
                <button
                  className="sts-arrow sts-arrow--next sts-arrow--v2"
                  onClick={() => handleNavClick('next')}
                  aria-label="Next"
                >
                  &#8250;
                </button>
              </>
            )}

            {mediaUrls[currIndex].endsWith('.webm') ? (
              <video
                key={mediaUrls[currIndex]}
                ref={el => {
                  mainVideoRef.current = el;
                  if (videoRef) {
                    videoRef.current = el;
                  }
                  watchFrameDuration(el);
                }}
                autoPlay={isPlaying}
                controls
                className="sts-media rounded-lg"
                loop
                disablePictureInPicture
                controlsList="nofullscreen"
                onLoadedData={e => {
                  applyRate(e.currentTarget, mediaUrls[currIndex]);
                  if (!isPlaying) {
                    e.currentTarget.pause();
                  }
                }}
              >
                <source
                  src={mediaUrls[currIndex]}
                  type="video/webm"
                />
                Your browser does not support video.
              </video>
            ) : (
              <img
                src={mediaUrls[currIndex]}
                alt={`Media ${currIndex}`}
                className="sts-media rounded-lg"
              />
            )}
          </div>
        </div>

        {/* Variant 1 & 3: arrows outside image */}
        {ARROW_VARIANT !== 2 && (
          <button
            className={`sts-arrow sts-arrow--next sts-arrow--v${ARROW_VARIANT}`}
            onClick={() => handleNavClick('next')}
            aria-label="Next"
          >
            &#8250;
          </button>
        )}
      </div>
    </div>
  );
};

export default SizeToScreen;
